<template>
  <div class="alert" :class="`alert-${variant}`" :role="role">
    <span v-if="$slots.icon" class="alert-icon" aria-hidden="true">
      <slot name="icon" />
    </span>
    <div class="alert-body">
      <p v-if="title" class="alert-title">{{ title }}</p>
      <div class="alert-content"><slot>{{ message }}</slot></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"

/**
 * Inline callout/notice box (wraps the global .alert vocabulary) — the amber
 * "no email set" / ruby error / forest "all settled" boxes every panel used to
 * hand-roll from a tinted `<div>` + border + icon. Pick a semantic variant;
 * pass a #icon slot for the leading glyph, `title` for a bold lead line, and
 * `message` (or the default slot) for the body. `role` defaults to "alert" for
 * warning/danger and "status" otherwise — override when needed.
 */
const props = withDefaults(
  defineProps<{
    variant?: "info" | "success" | "warning" | "danger"
    title?: string
    message?: string
    /** ARIA role; defaults to "alert" for warning/danger, "status" otherwise. */
    role?: string
  }>(),
  { variant: "info" },
)

const role = computed(
  () => props.role ?? (props.variant === "danger" || props.variant === "warning" ? "alert" : "status"),
)
</script>
